<template>
  <div class="rich-text-content" v-html="safeHtml"></div>
</template>

<script setup>
/* global defineProps */
import { computed } from 'vue'

const props = defineProps({
  html: {
    type: String,
    default: ''
  },
  fallback: {
    type: String,
    default: ''
  }
})

const allowedTags = new Set(['A', 'B', 'STRONG', 'I', 'EM', 'U', 'P', 'BR', 'UL', 'OL', 'LI', 'H3'])

const normalizeUrl = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const candidate = /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw}`

  try {
    const url = new URL(candidate)
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) return ''
    return url.href
  } catch {
    return ''
  }
}

const sanitizeNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent || '')
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return document.createTextNode('')
  }

  const tagName = node.tagName.toUpperCase()
  const nextTag = allowedTags.has(tagName) ? tagName.toLowerCase() : 'span'
  const nextNode = document.createElement(nextTag)

  if (tagName === 'A') {
    const href = normalizeUrl(node.getAttribute('href') || '')
    if (href) {
      nextNode.setAttribute('href', href)
      nextNode.setAttribute('target', '_blank')
      nextNode.setAttribute('rel', 'noopener noreferrer')
    }
  }

  Array.from(node.childNodes).forEach((child) => {
    nextNode.appendChild(sanitizeNode(child))
  })

  if (nextTag === 'span') {
    const fragment = document.createDocumentFragment()
    Array.from(nextNode.childNodes).forEach((child) => fragment.appendChild(child))
    return fragment
  }

  return nextNode
}

const sanitizeHtml = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')

  const clean = document.createElement('div')
  Array.from(container.childNodes).forEach((node) => {
    clean.appendChild(sanitizeNode(node))
  })

  return clean.innerHTML
}

const plainText = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return (container.textContent || '').trim()
}

const safeHtml = computed(() => {
  const source = plainText(props.html) ? props.html : props.fallback
  return sanitizeHtml(source)
})
</script>

<style scoped>
.rich-text-content {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.rich-text-content :deep(h3) {
  margin: 0.5rem 0;
  font-size: 1.1em;
  font-weight: 700;
  line-height: 1.4;
}

.rich-text-content :deep(p) {
  margin: 0.35rem 0;
}

.rich-text-content :deep(ul),
.rich-text-content :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  list-style-position: outside;
}

.rich-text-content :deep(ul) {
  list-style-type: disc;
}

.rich-text-content :deep(ol) {
  list-style-type: decimal;
}

.rich-text-content :deep(li) {
  display: list-item;
  margin: 0.2rem 0;
}

.rich-text-content :deep(a) {
  color: #c73d3d;
  font-weight: 600;
  text-decoration: underline;
}
</style>
